import { NewHire, OnboardingDoc, OnboardingHireStep } from '@core/models';

/**
 * THE single source for "how far along is this new hire" — used by My Onboarding
 * (progress bars) and the sidebar badge (items left to complete) so both agree.
 * "Remaining" = unsigned applicable documents + incomplete non-document tasks.
 */

/** Whether an onboarding document applies to a given hire (tier / minor rules). */
export function docAppliesToHire(doc: OnboardingDoc, hire: NewHire): boolean {
  if (doc.appliesTo === 'all') return true;
  if (doc.appliesTo === 'field') return hire.tier === 'Field' || hire.tier === 'Trade';
  if (doc.appliesTo === 'minor-only') return !!hire.age && hire.age < 18;
  return false;
}

/** The documents a hire must sign. */
export function applicableDocs(hire: NewHire, docs: OnboardingDoc[]): OnboardingDoc[] {
  return docs.filter((d) => docAppliesToHire(d, hire));
}

/** Whether a hire has satisfied a document's signature requirement. */
export function isDocSigned(hire: NewHire, doc: OnboardingDoc): boolean {
  const sig = hire.signatures?.[doc.id];
  if (!sig) return false;
  if (sig.status === 'signed') return true;
  return sig.status === 'employee-only' && !(hire.isMinor && doc.requiresParentIfMinor);
}

/** The authored onboarding steps assigned to the new hire themselves. */
export function onboardingEmployeeSteps(hire: NewHire): OnboardingHireStep[] {
  return (hire.steps ?? []).filter((s) => s.assignee?.kind === 'employee');
}

/** A step is complete when done AND signed off by the final reviewer — every
 *  step takes a final review, so there is no per-step flag to consult. */
export function isStepComplete(hire: NewHire, step: OnboardingHireStep): boolean {
  const r = hire.stepRecords?.[step.id];
  return !!r?.done && !!r.finalBy;
}

export interface OnboardingProgress {
  totalDocs: number;
  signedDocs: number;
  totalTasks: number;
  doneTasks: number;
  /** Items still to complete = unsigned docs + incomplete tasks. */
  remaining: number;
}

/** Roll up a hire's onboarding progress (0-safe when hire/docs are missing). */
export function onboardingProgress(hire: NewHire | null | undefined, docs: OnboardingDoc[]): OnboardingProgress {
  if (!hire) return { totalDocs: 0, signedDocs: 0, totalTasks: 0, doneTasks: 0, remaining: 0 };
  const ap = applicableDocs(hire, docs);
  const signedDocs = ap.filter((d) => isDocSigned(hire, d)).length;
  const tasks = onboardingEmployeeSteps(hire);
  const doneTasks = tasks.filter((t) => isStepComplete(hire, t)).length;
  return {
    totalDocs: ap.length,
    signedDocs,
    totalTasks: tasks.length,
    doneTasks,
    remaining: ap.length - signedDocs + (tasks.length - doneTasks),
  };
}
